import { useEffect, useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { useGetPlansQuery } from 'redux/apis/users/users-api';
import { ALL_PLANS_QUERY_PARAMS } from 'redux/apis/users/users-types';

import { Grid, Typography } from '@mui/material';

import DatePickerController from 'controllers/date-picker-controller';
import { OutlinedInputController } from 'controllers/outlined-input-controller';
import { SelectController } from 'controllers/select-controller';

import { datePickerDefaultState, planStatusDefaultState } from './form-config/data';

import { configurePaymentDetailsForm } from './form-config/utils';
import { findPlanById, planNamesAdapter } from './utils';

import { PlanNameEnum, PlanStatusEnum } from 'pages/users/entities';

import { IUser, IUserPlan } from 'utils/types';

import { SwitchController } from 'controllers/switch-controller';
import { planDetailsStyles } from './styles';

interface NewType {
  userPlan: IUserPlan | null;
  user: IUser;
  onStatusChanged: (status: PlanStatusEnum) => void;
}

export const PlanDetails = ({ userPlan, user, onStatusChanged }: NewType) => {
  const plans = useGetPlansQuery(ALL_PLANS_QUERY_PARAMS)?.data?.items || [];
  const { control, setValue } = useFormContext();

  const isPlanExist = !!userPlan;
  const userTrialEndDate = userPlan?.trialEndDate;
  const userNextChargeDate = userPlan?.nextChargeDate;
  const isPlanPending = userPlan?.isPendingForCancel;
  const isFirstProjectFree = userPlan?.name === PlanNameEnum.FIRST_PROJECT_FREE;
  const isLifeTimeDeal = userPlan?.name === PlanNameEnum.LIFETIME_DEAL;

  const tempPlanName = useWatch({ control: control, name: 'tempPlanName' });
  const { prevTempPlanStatus = '', currentTempPlanStatus = '' } =
    useWatch({
      control: control,
      name: 'tempPlanStatus',
    }) || {};

  const [plansNameProps, setPlansNameProps] = useState({
    data: planNamesAdapter(plans),
    disabled: true,
  });
  const [plansStatusProps, setPlansStatusProps] = useState(planStatusDefaultState);
  const [datePickerProps, setDatePickerProps] = useState(datePickerDefaultState);

  const isOverrideProjectCount = useWatch({
    control: control,
    name: 'isOverride',
  });

  useEffect(() => {
    if (isPlanExist && !isFirstProjectFree) {
      configurePaymentDetailsForm(
        {
          planName: tempPlanName,
          planStatus: currentTempPlanStatus,
          prevPlanStatus: prevTempPlanStatus,
        },
        { userTrialEndDate, userNextChargeDate },
        { setFormValue: setValue },
        {
          setPlansNameProps,
          setPlansStatusProps,
          setDatePickerProps,
        }
      );
    }
  }, [
    isPlanExist,
    isFirstProjectFree,
    tempPlanName,
    currentTempPlanStatus,
    prevTempPlanStatus,
    userTrialEndDate,
    userNextChargeDate,
    setValue,
  ]);

  const onAdditionalPlanNameChange = (id: number) => {
    const selectedPlan = findPlanById(plans, id);
    setValue('tempPlanName', selectedPlan?.name);
  };

  const onAdditionalPlanStatusChange = (status: PlanStatusEnum) => {
    setValue('tempPlanStatus', {
      prevTempPlanStatus: currentTempPlanStatus,
      currentTempPlanStatus: status,
    });

    onStatusChanged(status);
  };

  const handleMaxProjectsCountChange = (e: any) => {
    if (Number(e?.target?.value) < Number(user.activeProjectsCount)) {
      setValue('maxProjectsCount', user.activeProjectsCount);
      e?.stopPropagation();
      e?.preventDefault();

      return;
    }

    if (e?.target?.value > 0) {
      setValue('maxProjectsCount', e?.target?.value);
    }
  };

  const getPlanInfoMessage = () => {
    if (!isPlanExist) return 'The user is without any plan.';
    if (isFirstProjectFree) return 'The user has the First Project Free plan.';
    if (isLifeTimeDeal) return 'The user has the Life Time Deal plan.';
    if (isPlanPending) return `The user's plan is pending.`;
  };

  if (!isPlanExist || isFirstProjectFree || isLifeTimeDeal) {
    const planInfoMessage = getPlanInfoMessage();
    return (
      <Grid sx={planDetailsStyles.userPlanInfo}>
        {`${planInfoMessage}\nThere are no details to update.`}
      </Grid>
    );
  }

  return (
    <Grid sx={planDetailsStyles.container}>
      <SelectController
        required
        fullWidth
        withError
        disabled={plansNameProps.disabled}
        placeholder="Plan Name"
        name={'plan.productId'}
        label="Plan Name"
        options={plansNameProps.data}
        additionalOnChange={onAdditionalPlanNameChange}
      />

      {userPlan.status !== PlanStatusEnum.INACTIVE && (
        <Grid sx={planDetailsStyles.activeProjectContainer}>
          <Typography sx={planDetailsStyles.itemTitle}>Active Projects</Typography>

          <Grid sx={planDetailsStyles.switchContainer}>
            <Typography sx={planDetailsStyles.itemLabel}>Override</Typography>

            <SwitchController shouldUnregister name="isOverride" />
          </Grid>

          {isOverrideProjectCount && (
            <OutlinedInputController
              fullWidth
              withError
              type="number"
              name="maxProjectsCount"
              placeholder="Doe"
              inputProps={{ min: user.activeProjectsCount }}
              additionalOnChange={handleMaxProjectsCountChange}
            />
          )}
        </Grid>
      )}

      <SelectController
        required
        fullWidth
        withError
        disabled={plansStatusProps.disabled}
        placeholder="Plan Status"
        name={'plan.status'}
        label="Plan Status"
        options={plansStatusProps.data}
        additionalOnChange={onAdditionalPlanStatusChange}
      />
      {/* {currentTempPlanStatus === PlanStatusEnum.INACTIVE && <div>TEST</div>} */}

      <Grid sx={planDetailsStyles.dateAndSubscriptionId}>
        <OutlinedInputController
          fullWidth
          withError
          name={'plan.invoiceId'}
          label="Subscription ID"
          placeholder="PK-PXXXXXXXXX"
          disabled={!datePickerProps.isVisible}
        />

        <DatePickerController
          withError
          fullWidth
          name={datePickerProps.name}
          label={datePickerProps.label}
          key={datePickerProps.key}
          minDate={datePickerProps.minDate}
          disabled={!datePickerProps.isVisible}
        />
      </Grid>
    </Grid>
  );
};
